import React, { useEffect, useState } from 'react';
import { attendanceAPI, getActiveStaff, getPendingLeaves, announcementAPI, leaveAPI, salaryAPI, leaveBalanceAPI, loanAPI, reimbursementAPI, grievanceAPI, helpdeskAPI, overtimeAPI, managerAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '../components/Skeleton';

const PENDING_TYPES = [
  { key: 'LEAVE',         label: 'Leave',        icon: '📋', color: '#3b82f6', bg: '#dbeafe' },
  { key: 'LOAN',          label: 'Loan',          icon: '💰', color: '#f59e0b', bg: '#fef3c7' },
  { key: 'OVERTIME',      label: 'Overtime',      icon: '⏰', color: '#8b5cf6', bg: '#ede9fe' },
  { key: 'REIMBURSEMENT', label: 'Reimbursement', icon: '🧾', color: '#10b981', bg: '#d1fae5' },
  { key: 'GRIEVANCE',     label: 'Grievance',     icon: '⚠️', color: '#ef4444', bg: '#fee2e2' },
  { key: 'HELPDESK',      label: 'Helpdesk',      icon: '🎫', color: '#06b6d4', bg: '#e0f2fe' },
];

function PendingSummaryCards() {
  const navigate = useNavigate();
  const now = new Date();
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      leaveAPI.getPending().catch(() => ({ data: [] })),
      loanAPI.getPending().catch(() => ({ data: [] })),
      overtimeAPI.getByMonth(now.getMonth() + 1, now.getFullYear()).catch(() => ({ data: [] })),
      reimbursementAPI.getByStatus('PENDING').catch(() => ({ data: [] })),
      grievanceAPI.getAll().catch(() => ({ data: [] })),
      helpdeskAPI.getByStatus('OPEN').catch(() => ({ data: [] })),
    ]).then(([lv, ln, ot, re, gr, hd]) => {
      setCounts({
        LEAVE:         (lv.data || []).length,
        LOAN:          (ln.data || []).length,
        OVERTIME:      (ot.data || []).filter(r => r.status === 'PENDING').length,
        REIMBURSEMENT: (re.data || []).length,
        GRIEVANCE:     (gr.data || []).filter(r => ['OPEN','PENDING','IN_PROGRESS'].includes(r.status)).length,
        HELPDESK:      (hd.data || []).length,
      });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const total = Object.values(counts).reduce((s, v) => s + v, 0);

  const MODULES = [
    { key: 'LEAVE',         label: 'Leave',         sub: 'Approval pending', icon: '📋', color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe', path: '/leave' },
    { key: 'OVERTIME',      label: 'Overtime',       sub: 'Awaiting review',  icon: '⏰', color: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe', path: '/overtime' },
    { key: 'REIMBURSEMENT', label: 'Reimbursement',  sub: 'Claims pending',   icon: '🧾', color: '#10b981', bg: '#f0fdf4', border: '#bbf7d0', path: '/reimbursements' },
    { key: 'LOAN',          label: 'Loan & Advance', sub: 'Requests pending', icon: '💰', color: '#f59e0b', bg: '#fffbeb', border: '#fde68a', path: '/loans' },
    { key: 'GRIEVANCE',     label: 'Grievances',     sub: 'Open cases',       icon: '⚠️', color: '#ef4444', bg: '#fff5f5', border: '#fecdd3', path: '/grievances' },
    { key: 'HELPDESK',      label: 'HR Helpdesk',    sub: 'Open tickets',     icon: '🎫', color: '#06b6d4', bg: '#ecfeff', border: '#a5f3fc', path: '/helpdesk' },
  ];

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Section header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 16, color: '#0f172a' }}>🔔 Pending Requests Overview</div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
            {loading ? 'Loading...' : total > 0 ? `${total} total requests need your attention` : 'All caught up — no pending requests!'}
          </div>
        </div>
        <button onClick={() => navigate('/pending-requests')} style={{ padding: '9px 18px', borderRadius: 10, background: 'linear-gradient(135deg,#1a237e,#1565c0)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13, boxShadow: '0 4px 12px rgba(26,35,126,0.25)' }}>
          View All Requests →
        </button>
      </div>

      {/* Cards grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
        {MODULES.map(m => {
          const count = counts[m.key] ?? 0;
          return (
            <div key={m.key}
              onClick={() => navigate(count > 0 ? `/pending-requests?type=${m.key}` : m.path)}
              style={{
                background: '#fff', borderRadius: 16, padding: '18px 20px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                border: count > 0 ? `1.5px solid ${m.border}` : '1.5px solid #f1f5f9',
                cursor: 'pointer', transition: 'all 0.15s',
                display: 'flex', alignItems: 'center', gap: 16,
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.1)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.06)'; }}
            >
              {/* Icon box */}
              <div style={{ width: 52, height: 52, borderRadius: 14, background: count > 0 ? m.bg : '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>
                {m.icon}
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a', marginBottom: 2 }}>{m.label}</div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>{m.sub}</div>
              </div>

              {/* Count */}
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                {loading ? (
                  <div style={{ width: 36, height: 28, background: '#f1f5f9', borderRadius: 8 }} />
                ) : (
                  <>
                    <div style={{ fontSize: 26, fontWeight: 900, lineHeight: 1, color: count > 0 ? m.color : '#e2e8f0' }}>{count}</div>
                    {count > 0
                      ? <div style={{ fontSize: 10, fontWeight: 700, color: m.color, marginTop: 3 }}>Pending</div>
                      : <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>All clear</div>
                    }
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeeklyTrendChart({ data }) {
  if (!data.length) return null;
  const max = Math.max(...data.map(d => d.total), 1);
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', flex: 1 }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', marginBottom: 4 }}>7-Day Attendance Trend</div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 20 }}>Present vs Absent last 7 days</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 100 }}>
        {data.map((d, i) => {
          const pct = d.total > 0 ? (d.present / d.total) * 100 : 0;
          const barH = Math.max(4, (d.total / max) * 90);
          const presentH = (d.present / Math.max(d.total, 1)) * barH;
          const label = new Date(d.date).getDay();
          return (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div style={{ fontSize: 10, color: pct > 80 ? '#10b981' : pct > 60 ? '#f59e0b' : '#ef4444', fontWeight: 700 }}>
                {d.total > 0 ? `${Math.round(pct)}%` : ''}
              </div>
              <div style={{ width: '100%', position: 'relative', height: barH, borderRadius: 4, background: '#fee2e2', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: presentH, background: '#10b981', borderRadius: 4, transition: 'height 0.8s ease' }} />
              </div>
              <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>{days[label]}</div>
              <div style={{ fontSize: 9, color: '#cbd5e1' }}>{d.date.slice(5)}</div>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 16, marginTop: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#64748b' }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: '#10b981' }} /> Present
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#64748b' }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: '#fee2e2' }} /> Absent
        </div>
      </div>
    </div>
  );
}

function DeptChart({ data }) {
  if (!data.length) return null;
  const total = data.reduce((s, d) => s + d.count, 0) || 1;
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899'];
  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', flex: 1 }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', marginBottom: 4 }}>Department Strength</div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 18 }}>Staff count per department</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {data.slice(0, 6).map((d, i) => {
          const pct = Math.round((d.count / total) * 100);
          const color = COLORS[i % COLORS.length];
          return (
            <div key={i}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                <span style={{ color: '#475569', fontWeight: 500, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</span>
                <span style={{ color, fontWeight: 700 }}>{d.count}</span>
              </div>
              <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3 }}>
                <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.8s ease' }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, sub, color, bg, onClick }) {
  return (
    <div onClick={onClick} style={{
      background: '#fff', borderRadius: 16, padding: '22px 24px',
      boxShadow: '0 2px 16px rgba(0,0,0,0.06)', flex: 1, minWidth: 160,
      borderTop: `4px solid ${color}`, cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.15s, box-shadow 0.15s',
      position: 'relative', overflow: 'hidden',
    }}
      onMouseEnter={e => { if (onClick) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)'; }}}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 2px 16px rgba(0,0,0,0.06)'; }}
    >
      <div style={{
        position: 'absolute', right: -10, top: -10, width: 70, height: 70,
        background: bg, borderRadius: '50%', display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontSize: 26,
      }}>{icon}</div>
      <div style={{ fontSize: 30, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>{value}</div>
      <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color, fontWeight: 700, marginTop: 6, background: bg, display: 'inline-block', padding: '2px 10px', borderRadius: 20 }}>{sub}</div>}
    </div>
  );
}

function StaffDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ leaves: [], balances: [], salary: null, announcements: [], myAttendance: [] });
  const [loading, setLoading] = useState(true);
  const [managerPending, setManagerPending] = useState(null);
  const staffId = user?.staffId;
  const today = new Date().toISOString().split('T')[0];
  const now = new Date();
  const month = now.getMonth() + 1;
  const year = now.getFullYear();
  const hour = now.getHours();
  const greeting = hour < 12 ? 'Good Morning' : hour < 17 ? 'Good Afternoon' : 'Good Evening';

  useEffect(() => {
    if (!staffId) return;
    Promise.all([
      leaveAPI.getByStaff(staffId).catch(() => ({ data: [] })),
      leaveBalanceAPI.getStaff(staffId, year).catch(() => ({ data: [] })),
      salaryAPI.getSlip(staffId, month, year).catch(() => ({ data: null })),
      announcementAPI.getActive().catch(() => ({ data: [] })),
      attendanceAPI.getMonthly(staffId, month, year).catch(() => ({ data: [] })),
    ]).then(([lv, bal, sal, ann, att]) => {
      const slips = Array.isArray(sal.data) ? sal.data : sal.data ? [sal.data] : [];
      setData({
        leaves: lv.data || [],
        balances: bal.data || [],
        salary: slips[0] || null,
        announcements: ann.data || [],
        myAttendance: att.data || [],
      });
      setLoading(false);
    }).catch(() => setLoading(false));
    // Manager ke liye team pending requests load karo
    if (user?.role === 'MANAGER') {
      managerAPI.getPending().then(r => {
        const d = r.data || {};
        setManagerPending({
          total:   d.totalPendingCount || 0,
          leaves:  (d.pendingLeaves?.length || 0) + (d.pendingLeaveRequests?.length || 0),
          overtime: d.pendingOvertimes?.length || 0,
          reimb:   d.pendingReimbursements?.length || 0,
          loans:   d.pendingLoans?.length || 0,
        });
      }).catch(() => {});
    }
  }, [staffId]);

  const presentDays = data.myAttendance.filter(a => a.status === 'PRESENT').length;
  const absentDays  = data.myAttendance.filter(a => a.status === 'ABSENT').length;
  const halfDays    = data.myAttendance.filter(a => a.status === 'HALF_DAY').length;
  const pendingLeaves = data.leaves.filter(l => l.status === 'PENDING').length;
  const approvedLeaves = data.leaves.filter(l => l.status === 'APPROVED').length;
  const totalWorked = data.myAttendance.length;
  const attendanceRate = totalWorked > 0 ? Math.round((presentDays / totalWorked) * 100) : 0;

  const STAT_CARDS = [
    { icon: '✅', label: 'Present This Month', value: presentDays, color: '#10b981', bg: '#d1fae5', path: '/attendance' },
    { icon: '❌', label: 'Absent This Month',  value: absentDays,  color: '#ef4444', bg: '#fee2e2' },
    { icon: '◑',  label: 'Half Days',          value: halfDays,    color: '#f59e0b', bg: '#fef3c7' },
    { icon: '📋', label: 'Pending Leaves',     value: pendingLeaves, color: '#3b82f6', bg: '#dbeafe', path: '/leave' },
  ];

  return (
    <div>
      {/* Hero */}
      <div style={{
        background: 'linear-gradient(135deg, #0a1628 0%, #1a237e 60%, #1565c0 100%)',
        borderRadius: 20, padding: '28px 32px', marginBottom: 24,
        boxShadow: '0 8px 32px rgba(26,35,126,0.35)', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', right: -30, top: -30, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
        <div style={{ position: 'relative' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 6 }}>
            {greeting}, {user?.username} 👋
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>
            {now.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
          <div style={{ display: 'flex', gap: 14, marginTop: 20, flexWrap: 'wrap' }}>
            {[
              { label: 'Attendance Rate', value: `${attendanceRate}%`, color: attendanceRate > 80 ? '#6ee7b7' : '#fca5a5' },
              { label: 'Days Present',    value: presentDays,          color: '#6ee7b7' },
              { label: 'Approved Leaves', value: approvedLeaves,       color: '#93c5fd' },
              { label: 'Net Salary',      value: data.salary ? `₹${parseFloat(data.salary.netSalary || 0).toLocaleString('en-IN')}` : '—', color: '#fcd34d' },
            ].map(s => (
              <div key={s.label} style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 12, padding: '10px 20px', backdropFilter: 'blur(10px)' }}>
                <div style={{ color: s.color, fontSize: 22, fontWeight: 800 }}>{s.value}</div>
                <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Manager Team Inbox Section */}
      {user?.role === 'MANAGER' && managerPending !== null && (
        <div style={{ background: '#fff', borderRadius: 18, boxShadow: '0 2px 16px rgba(0,0,0,0.07)', marginBottom: 24, overflow: 'hidden' }}>
          {/* Header bar */}
          <div style={{ padding: '16px 22px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: managerPending.total > 0 ? '#fef3c7' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>👔</div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>Team Pending Requests</div>
                <div style={{ fontSize: 12, color: managerPending.total > 0 ? '#b45309' : '#64748b', marginTop: 2 }}>
                  {managerPending.total > 0
                    ? `${managerPending.total} request${managerPending.total > 1 ? 's' : ''} waiting for your approval`
                    : '✓ No pending requests — your team is all clear'}
                </div>
              </div>
            </div>
            <button onClick={() => navigate('/manager-inbox')} style={{ padding: '9px 20px', borderRadius: 10, background: 'linear-gradient(135deg, #1a237e, #1565c0)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13, boxShadow: '0 4px 12px rgba(26,35,126,0.25)', whiteSpace: 'nowrap' }}>
              Open Inbox →
            </button>
          </div>

          {/* 4 type cards in a row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' }}>
            {[
              { label: 'Leave Requests', icon: '📋', color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe', count: managerPending.leaves,   type: 'LEAVE' },
              { label: 'Overtime',       icon: '⏰', color: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe', count: managerPending.overtime, type: 'OVERTIME' },
              { label: 'Reimbursement',  icon: '🧾', color: '#10b981', bg: '#f0fdf4', border: '#bbf7d0', count: managerPending.reimb,    type: 'REIMBURSEMENT' },
              { label: 'Loan & Advance', icon: '💰', color: '#f59e0b', bg: '#fffbeb', border: '#fde68a', count: managerPending.loans,    type: 'LOAN' },
            ].map((t, i, arr) => (
              <div key={t.label}
                onClick={() => navigate(`/manager-inbox?type=${t.type}`)}
                style={{
                  padding: '18px 20px', cursor: 'pointer',
                  borderRight: i < arr.length - 1 ? '1px solid #f1f5f9' : 'none',
                  background: '#fff', transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = t.bg}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: 22 }}>{t.icon}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>{t.label}</span>
                </div>
                <div style={{ fontSize: 32, fontWeight: 900, color: t.count > 0 ? t.color : '#e2e8f0', lineHeight: 1 }}>{t.count}</div>
                <div style={{ marginTop: 8 }}>
                  {t.count > 0
                    ? <span style={{ fontSize: 11, fontWeight: 700, color: t.color, background: t.bg, border: `1px solid ${t.border}`, padding: '3px 10px', borderRadius: 20 }}>
                        {t.count} Pending
                      </span>
                    : <span style={{ fontSize: 11, color: '#94a3b8' }}>No pending</span>
                  }
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
          {[1,2,3,4].map(i => <Skeleton key={i} height={100} radius={16} style={{ flex: 1 }} />)}
        </div>
      ) : (
        <>
          {/* Announcements */}
          {data.announcements.slice(0, 2).map(ann => (
            <div key={ann.id} style={{
              background: 'linear-gradient(90deg, #fffbeb, #fff)', border: '1px solid #fde68a',
              borderLeft: '4px solid #f59e0b', borderRadius: 12, padding: '14px 20px',
              marginBottom: 12, display: 'flex', gap: 12, alignItems: 'flex-start',
            }}>
              <span style={{ fontSize: 20 }}>📢</span>
              <div>
                <div style={{ fontWeight: 700, color: '#92400e', fontSize: 14 }}>{ann.title}</div>
                <div style={{ color: '#78350f', fontSize: 13, marginTop: 3 }}>{ann.content}</div>
              </div>
            </div>
          ))}

          {/* Stat Cards */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
            {STAT_CARDS.map(c => (
              <div key={c.label} onClick={() => c.path && navigate(c.path)} style={{
                background: '#fff', borderRadius: 16, padding: '22px 24px',
                boxShadow: '0 2px 16px rgba(0,0,0,0.06)', flex: 1, minWidth: 150,
                borderTop: `4px solid ${c.color}`, cursor: c.path ? 'pointer' : 'default',
                position: 'relative', overflow: 'hidden',
                transition: 'transform 0.15s, box-shadow 0.15s',
              }}
                onMouseEnter={e => { if (c.path) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)'; }}}
                onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 2px 16px rgba(0,0,0,0.06)'; }}
              >
                <div style={{ position: 'absolute', right: -10, top: -10, width: 60, height: 60, background: c.bg, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{c.icon}</div>
                <div style={{ fontSize: 30, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>{c.value}</div>
                <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>{c.label}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            {/* Leave Balances */}
            <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', flex: 1, minWidth: 280 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', marginBottom: 16 }}>Leave Balances ({year})</div>
              {data.balances.length === 0 ? (
                <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No balance data</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {data.balances.map(b => {
                    const pct = b.totalDays > 0 ? Math.round((b.remainingDays / b.totalDays) * 100) : 0;
                    const color = pct > 50 ? '#10b981' : pct > 20 ? '#f59e0b' : '#ef4444';
                    return (
                      <div key={b.id}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                          <span style={{ color: '#475569', fontWeight: 600 }}>{b.leaveType}</span>
                          <span style={{ color, fontWeight: 700 }}>{b.remainingDays}/{b.totalDays} days left</span>
                        </div>
                        <div style={{ height: 7, background: '#f1f5f9', borderRadius: 4 }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.8s' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', flex: 1, minWidth: 220 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', marginBottom: 16 }}>Quick Actions</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { label: 'Apply Leave',    icon: '📋', path: '/leave',       color: '#3b82f6', bg: '#dbeafe' },
                  { label: 'View Salary',    icon: '₹',  path: '/salary',      color: '#f59e0b', bg: '#fef3c7' },
                  { label: 'Raise Grievance',icon: '◎', path: '/grievances',  color: '#8b5cf6', bg: '#ede9fe' },
                  { label: 'My Training',    icon: '⊠', path: '/training',    color: '#10b981', bg: '#d1fae5' },
                ].map(a => (
                  <button key={a.path} onClick={() => navigate(a.path)} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                    background: a.bg, border: 'none', borderRadius: 10, cursor: 'pointer',
                    color: a.color, fontWeight: 600, fontSize: 13, textAlign: 'left',
                    transition: 'transform 0.15s',
                  }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.02)'}
                    onMouseLeave={e => e.currentTarget.style.transform = ''}
                  >
                    <span style={{ fontSize: 18 }}>{a.icon}</span> {a.label}
                  </button>
                ))}
              </div>
            </div>

            {/* My Recent Leaves */}
            {data.leaves.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', flex: 2, minWidth: 300 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', marginBottom: 16 }}>My Recent Leaves</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {data.leaves.slice(0, 4).map(lv => {
                    const STATUS_COLOR = { PENDING: '#f59e0b', APPROVED: '#10b981', REJECTED: '#ef4444' };
                    const STATUS_BG = { PENDING: '#fef3c7', APPROVED: '#d1fae5', REJECTED: '#fee2e2' };
                    return (
                      <div key={lv.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#f8fafc', borderRadius: 10 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_COLOR[lv.status] || '#94a3b8', flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{lv.leaveType} Leave</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>{lv.fromDate} → {lv.toDate}</div>
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 700, background: STATUS_BG[lv.status], color: STATUS_COLOR[lv.status], padding: '3px 10px', borderRadius: 20 }}>{lv.status}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user, isAdmin } = useAuth();

  // Staff sees their personal dashboard
  if (!isAdmin()) return <StaffDashboard />;

  // Admin sees the full company dashboard below
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [staff, setStaff] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trendData, setTrendData] = useState([]);
  const [deptData, setDeptData] = useState([]);

  useEffect(() => {
    Promise.all([
      attendanceAPI.getDashboard().catch(() => ({ data: null })),
      getActiveStaff().catch(() => ({ data: [] })),
      getPendingLeaves().catch(() => ({ data: [] })),
      announcementAPI.getActive().catch(() => ({ data: [] })),
    ]).then(([ds, st, lv, ann]) => {
      setStats(ds.data);
      setStaff(st.data || []);
      setLeaves(lv.data || []);
      setAnnouncements(ann.data || []);
      setLoading(false);

      // Build dept chart from staff data
      const deptMap = {};
      (st.data || []).forEach(s => {
        const n = s.department?.name || 'Unassigned';
        deptMap[n] = (deptMap[n] || 0) + 1;
      });
      setDeptData(Object.entries(deptMap).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count));

      // Fetch last 7 days trend
      const dates = Array.from({ length: 7 }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - 6 + i);
        return d.toISOString().split('T')[0];
      });
      Promise.all(dates.map(date =>
        attendanceAPI.getByDate(date).then(r => {
          const records = r.data || [];
          const present = records.filter(rec => rec.status === 'PRESENT' || rec.status === 'HALF_DAY').length;
          return { date, present, total: records.length };
        }).catch(() => ({ date, present: 0, total: 0 }))
      )).then(setTrendData);
    }).catch(() => setLoading(false));
  }, []);

  const present = stats?.presentToday || 0;
  const absent = stats?.absentToday || 0;
  const halfDay = stats?.halfDayToday || 0;
  const onLeave = stats?.onLeaveToday || 0;
  const totalStaff = stats?.totalStaff || staff.length;
  const rate = totalStaff > 0 ? Math.round((present / totalStaff) * 100) : 0;
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good Morning' : hour < 17 ? 'Good Afternoon' : 'Good Evening';

  const STATUS_BREAKDOWN = [
    { label: 'Present', count: present, color: '#10b981', bg: '#d1fae5' },
    { label: 'Absent',  count: absent,  color: '#ef4444', bg: '#fee2e2' },
    { label: 'Half Day',count: halfDay, color: '#f59e0b', bg: '#fef3c7' },
    { label: 'On Leave', count: onLeave, color: '#3b82f6', bg: '#dbeafe' },
  ];

  return (
    <div>
      {/* Hero Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #0a1628 0%, #1a237e 60%, #1565c0 100%)',
        borderRadius: 20, padding: '28px 32px', marginBottom: 24,
        boxShadow: '0 8px 32px rgba(26,35,126,0.35)', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', right: -30, top: -30, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
        <div style={{ position: 'absolute', right: 60, bottom: -50, width: 150, height: 150, borderRadius: '50%', background: 'rgba(59,130,246,0.1)' }} />
        <div style={{ position: 'relative' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 6 }}>
            {greeting}, {user?.username} 👋
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>
            {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
          <div style={{ display: 'flex', gap: 14, marginTop: 20 }}>
            {[
              { label: 'Total Staff', value: totalStaff, color: '#93c5fd' },
              { label: 'Present Today', value: present, color: '#6ee7b7' },
              { label: 'Pending Leaves', value: leaves.length, color: '#fcd34d' },
              { label: 'Attendance Rate', value: `${rate}%`, color: rate > 80 ? '#6ee7b7' : '#fca5a5' },
            ].map(s => (
              <div key={s.label} style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 12, padding: '10px 20px', backdropFilter: 'blur(10px)' }}>
                <div style={{ color: s.color, fontSize: 22, fontWeight: 800 }}>{s.value}</div>
                <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ display: 'flex', gap: 16 }}>
            {[1,2,3,4].map(i => <Skeleton key={i} height={100} radius={16} style={{ flex: 1 }} />)}
          </div>
          <div style={{ display: 'flex', gap: 20 }}>
            <Skeleton height={180} radius={16} style={{ flex: 1 }} />
            <Skeleton height={180} radius={16} style={{ flex: 1 }} />
            <Skeleton height={180} radius={16} style={{ flex: 1 }} />
          </div>
        </div>
      ) : (
        <>
          {/* Pending Requests Summary — hero ke neeche sabse pehle */}
          <PendingSummaryCards />

          {/* Announcements */}
          {announcements.map(ann => (
            <div key={ann.id} style={{
              background: 'linear-gradient(90deg, #fffbeb, #fff)',
              border: '1px solid #fde68a', borderLeft: '4px solid #f59e0b',
              borderRadius: 12, padding: '14px 20px', marginBottom: 12,
              display: 'flex', gap: 12, alignItems: 'flex-start',
            }}>
              <span style={{ fontSize: 20 }}>📢</span>
              <div>
                <div style={{ fontWeight: 700, color: '#92400e', fontSize: 14 }}>{ann.title}</div>
                <div style={{ color: '#78350f', fontSize: 13, marginTop: 3 }}>{ann.content}</div>
              </div>
            </div>
          ))}

          {/* KPI Cards */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
            <KpiCard icon="👥" label="Total Active Staff" value={totalStaff} color="#3b82f6" bg="#eff6ff" onClick={() => navigate('/staff')} />
            <KpiCard icon="✅" label="Present Today" value={present} color="#10b981" bg="#d1fae5" sub={`${rate}% rate`} onClick={() => navigate('/attendance')} />
            <KpiCard icon="❌" label="Absent Today" value={absent} color="#ef4444" bg="#fee2e2" />
            <KpiCard icon="🔔" label="Pending Leaves" value={leaves.length} color="#f59e0b" bg="#fef3c7" sub="Awaiting approval" onClick={() => navigate('/leave')} />
          </div>

          {/* Today's Breakdown + Rate */}
          <div style={{ display: 'flex', gap: 20, marginBottom: 24 }}>
            {/* Status Breakdown */}
            <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', marginBottom: 20 }}>Today's Status Breakdown</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {STATUS_BREAKDOWN.map(s => (
                  <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                    <div style={{ flex: 1, fontSize: 13, color: '#475569', fontWeight: 500 }}>{s.label}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: s.color }}>{s.count}</div>
                    <div style={{ width: 80 }}>
                      <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3 }}>
                        <div style={{
                          width: `${staff.length ? (s.count / staff.length) * 100 : 0}%`,
                          height: '100%', background: s.color, borderRadius: 3,
                          transition: 'width 1s ease',
                        }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Rate Gauge */}
            <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', marginBottom: 16 }}>Attendance Rate</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                <div style={{ position: 'relative', width: 100, height: 100 }}>
                  <svg width="100" height="100" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="38" fill="none" stroke="#f1f5f9" strokeWidth="10" />
                    <circle cx="50" cy="50" r="38" fill="none"
                      stroke={rate > 80 ? '#10b981' : rate > 60 ? '#f59e0b' : '#ef4444'}
                      strokeWidth="10" strokeLinecap="round"
                      strokeDasharray={`${(rate / 100) * 239} 239`}
                      transform="rotate(-90 50 50)"
                      style={{ transition: 'stroke-dasharray 1s ease' }}
                    />
                  </svg>
                  <div style={{
                    position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                  }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{rate}%</div>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>
                    <span style={{ fontWeight: 700, color: '#10b981' }}>{present}</span> present out of <span style={{ fontWeight: 700 }}>{staff.length}</span>
                  </div>
                  <div style={{
                    fontSize: 12, fontWeight: 700, padding: '4px 14px', borderRadius: 20, display: 'inline-block',
                    background: rate > 80 ? '#d1fae5' : rate > 60 ? '#fef3c7' : '#fee2e2',
                    color: rate > 80 ? '#065f46' : rate > 60 ? '#92400e' : '#991b1b',
                  }}>
                    {rate > 80 ? 'Excellent' : rate > 60 ? 'Average' : 'Needs Attention'}
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', marginBottom: 16 }}>Quick Actions</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { label: 'Mark Attendance', icon: '✅', path: '/attendance', color: '#10b981', bg: '#d1fae5' },
                  { label: 'Apply Leave', icon: '📋', path: '/leave', color: '#3b82f6', bg: '#dbeafe' },
                  { label: 'Generate Salary', icon: '💰', path: '/salary', color: '#f59e0b', bg: '#fef3c7', adminOnly: true },
                  { label: 'View Reports', icon: '📈', path: '/reports', color: '#8b5cf6', bg: '#ede9fe', adminOnly: true },
                ].filter(a => !a.adminOnly || isAdmin()).map(action => (
                  <button key={action.path} onClick={() => navigate(action.path)} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                    background: action.bg, border: 'none', borderRadius: 10, cursor: 'pointer',
                    color: action.color, fontWeight: 600, fontSize: 13, textAlign: 'left',
                    transition: 'transform 0.15s',
                  }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.02)'}
                    onMouseLeave={e => e.currentTarget.style.transform = ''}
                  >
                    <span style={{ fontSize: 18 }}>{action.icon}</span>
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Charts Row */}
          {(trendData.length > 0 || deptData.length > 0) && (
            <div style={{ display: 'flex', gap: 20, marginBottom: 24 }}>
              <WeeklyTrendChart data={trendData} />
              <DeptChart data={deptData} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
